import {
  DriverPotentialFullPlanEntry,
  DriverPotentialFullPlanEntryEndNode,
  DriverPotentialFullPlanEntrySolvedOrder,
  DriverPotentialFullPlanEntryStartNode,
  DriverPotentialPlanEntry,
  DriverPotentialRestrictedPlanEntry,
  DriverPotentialRestrictedPlanEntryEndNode,
  DriverPotentialRestrictedPlanEntryStartNode,
} from "../../../../../../../common/services/driver/potential-plan/driver-potential-plan";
import PlanEntryType from "../../../../../../../common/types/plan-entry-type";
import DriverDetailsCandidatureManagementPlanEntry, {
  DriverDetailsCandidatureManagementPlanEntryWaypoint,
} from "./types/driver-details-candidature-management-plan-entry";

const checkIsRestrictedResponsePlanEntry = (
  responsePlanEntry: DriverPotentialPlanEntry,
): responsePlanEntry is DriverPotentialRestrictedPlanEntry => {
  return responsePlanEntry.viewType === PlanEntryType.RESTRICTED;
};

const createWaypointForFullPlanEntryStartNode = (
  responseNode: DriverPotentialFullPlanEntryStartNode,
): DriverDetailsCandidatureManagementPlanEntryWaypoint => {
  return {
    uuid: responseNode.meta.id,
    addressDisplayName: responseNode.displayName,
    clientName: null,
    estimatedDate: new Date(responseNode.meta.estimatedTime),
    orderNo: null,
    plannedDate: responseNode.time ? new Date(responseNode.time) : null,
    plannedHaltingTime: responseNode.haltingTime ?? null,
  };
};

const createWaypointForFullPlanEntryEndNode = (
  responseNode: DriverPotentialFullPlanEntryEndNode,
): DriverDetailsCandidatureManagementPlanEntryWaypoint => {
  return {
    uuid: responseNode.meta.id,
    addressDisplayName: responseNode.displayName,
    clientName: null,
    estimatedDate: new Date(responseNode.meta.estimatedTime),
    orderNo: null,
    plannedDate: responseNode.time ? new Date(responseNode.time) : null,
    plannedHaltingTime: responseNode.haltingTime ?? null,
  };
};

const createWaypointForRestrictedPlanEntryStartNode = (
  responseNode: DriverPotentialRestrictedPlanEntryStartNode,
): DriverDetailsCandidatureManagementPlanEntryWaypoint => {
  return {
    uuid: responseNode.meta.id,
    addressDisplayName: responseNode.displayName,
    clientName: null,
    estimatedDate: new Date(responseNode.meta.estimatedTime),
    orderNo: null,
    plannedDate: responseNode.time ? new Date(responseNode.time) : null,
    plannedHaltingTime: responseNode.haltingTime ?? null,
  };
};

const createWaypointForRestrictedPlanEntryEndNode = (
  responseNode: DriverPotentialRestrictedPlanEntryEndNode,
): DriverDetailsCandidatureManagementPlanEntryWaypoint => {
  return {
    uuid: responseNode.meta.id,
    addressDisplayName: responseNode.displayName,
    clientName: null,
    estimatedDate: new Date(responseNode.meta.estimatedTime),
    orderNo: null,
    plannedDate: responseNode.time ? new Date(responseNode.time) : null,
    plannedHaltingTime: responseNode.haltingTime ?? null,
  };
};

const createWaypointsForFullPlanEntrySolvedOrder = (
  responseSolvedOrder: DriverPotentialFullPlanEntrySolvedOrder,
): DriverDetailsCandidatureManagementPlanEntryWaypoint[] => {
  const waypoints: DriverDetailsCandidatureManagementPlanEntryWaypoint[] = [];

  responseSolvedOrder.ride.seq.forEach((seqItem) => {
    const waypoint: DriverDetailsCandidatureManagementPlanEntryWaypoint = {
      uuid: seqItem.meta.id,
      clientName: responseSolvedOrder.cargoCompany.displayName,
      orderNo: responseSolvedOrder.transportingOrder.cargoOrder.humanId,
      addressDisplayName: seqItem.displayName,
      estimatedDate: new Date(seqItem.meta.estimatedTime),
      plannedHaltingTime: seqItem.haltingTime,
      plannedDate: seqItem.time ? new Date(seqItem.time) : null,
    };

    waypoints.push(waypoint);
  });

  return waypoints;
};

const createWaypointsForFullPlanEntrySolvedOrders = (
  responseSolvedOrders: DriverPotentialFullPlanEntrySolvedOrder[],
): DriverDetailsCandidatureManagementPlanEntryWaypoint[] => {
  return responseSolvedOrders
    .map((responseSolvedOrder) =>
      createWaypointsForFullPlanEntrySolvedOrder(responseSolvedOrder),
    )
    .flat();
};

const createPlanEntryWaypointsForRestrictedPlanEntry = (
  responseRestrictedPlanEntry: DriverPotentialRestrictedPlanEntry,
): DriverDetailsCandidatureManagementPlanEntryWaypoint[] => {
  const waypoints: DriverDetailsCandidatureManagementPlanEntryWaypoint[] = [];

  const startWaypoint = createWaypointForRestrictedPlanEntryStartNode(
    responseRestrictedPlanEntry.startNode,
  );

  waypoints.push(startWaypoint);

  const endWaypoint = createWaypointForRestrictedPlanEntryEndNode(
    responseRestrictedPlanEntry.endNode,
  );

  waypoints.push(endWaypoint);

  return waypoints;
};

const createPlanEntryWaypointsForFullPlanEntry = (
  responseFullPlanEntry: DriverPotentialFullPlanEntry,
): DriverDetailsCandidatureManagementPlanEntryWaypoint[] => {
  const waypoints: DriverDetailsCandidatureManagementPlanEntryWaypoint[] = [];

  const startWaypoint = createWaypointForFullPlanEntryStartNode(
    responseFullPlanEntry.startNode,
  );

  waypoints.push(startWaypoint);

  const orderWaypoints = createWaypointsForFullPlanEntrySolvedOrders(
    responseFullPlanEntry.solvedOrders,
  );

  waypoints.push(...orderWaypoints);

  const endWaypoint = createWaypointForFullPlanEntryEndNode(
    responseFullPlanEntry.endNode,
  );

  waypoints.push(endWaypoint);

  return waypoints;
};

const createPlanEntryWaypoints = (
  responsePlanEntry: DriverPotentialPlanEntry,
): DriverDetailsCandidatureManagementPlanEntryWaypoint[] => {
  const isResponsePlanEntryRestricted =
    checkIsRestrictedResponsePlanEntry(responsePlanEntry);

  if (isResponsePlanEntryRestricted) {
    return createPlanEntryWaypointsForRestrictedPlanEntry(responsePlanEntry);
  }

  return createPlanEntryWaypointsForFullPlanEntry(responsePlanEntry);
};

const createPlanEntry = (
  responsePlanEntry: DriverPotentialPlanEntry,
): DriverDetailsCandidatureManagementPlanEntry => {
  const waypoints = createPlanEntryWaypoints(responsePlanEntry);

  return {
    uuid: responsePlanEntry.id,
    id: responsePlanEntry.humanId,
    waypoints,
  };
};

const createPlanEntries = (
  responsePlanEntries: DriverPotentialPlanEntry[],
): DriverDetailsCandidatureManagementPlanEntry[] => {
  return responsePlanEntries.map(createPlanEntry);
};

const driverDetailsCandidatureManagementPlanEntriesAfterCandidatureApproveFactory =
  {
    createPlanEntries,
  };

export default driverDetailsCandidatureManagementPlanEntriesAfterCandidatureApproveFactory;
